import type { RoomLayoutSpot } from './RoomLayoutMap.types';

/**
 * Minimal backend spot shape for deriving the grid key (same rules as layout fetch mapping).
 */
export type ApiSpotLike = {
  spot_id?: string | number;
  id?: string | number;
  spot_name: string;
};

type SpotRawApiInput = {
  /** API payload (snake_case). */
  spot_name?: string;
  spot_id?: string | number;
};

type SpotRawMapInput = {
  /** Room-layout-map shape (`spotName`, `id`). */
  spotName?: string;
  id?: string | number;
};

/**
 * Wire payloads from booking/time-slots API JSON or `RoomLayoutSpot`.
 * Call {@link normalizeSpot} at boundaries; use {@link NormalizedSpot} for derived fields.
 */
export type SpotIdentityWire =
  | SpotRawApiInput
  | SpotRawMapInput
  | (SpotRawApiInput & SpotRawMapInput);

export type NormalizedSpot = {
  /** Backend `spot_id` / map `id` when present. */
  readonly apiSpotId: string | null;
  /** Display label: API `spot_name`, then map `spotName`. */
  readonly displayLabel: string | null;
  /**
   * Reserved-set / grid identity: api id first, then `spotName`, then `spot_name`
   * (matches {@link deriveSpotKeyFromApiSpot} when both UUID and label exist).
   */
  readonly gridKey: string | null;
};

function nonEmpty(value: string | undefined): string | null {
  if (value === undefined) return null;
  if (typeof value !== 'string') return null;
  if (value === '') return null;
  return value;
}

function idAsString(raw: unknown): string | null {
  if (typeof raw === 'string') {
    if (raw !== '') return raw;
    return null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return null;
}

/**
 * Canonical grid key for a backend layout spot (same string stored as `RoomLayoutSpot.spotName`).
 */
export function deriveSpotKeyFromApiSpot(spot: ApiSpotLike, index: number): string {
  const rawId = spot.spot_id ?? spot.id;
  const backendId = idAsString(rawId);
  return backendId ?? (spot.spot_name !== '' ? spot.spot_name : null) ?? String(index + 1);
}

/**
 * Grid / reserved / selection key for a normalized layout spot (equals `spot.spotName` from the map).
 */
export function getSpotKey(spot: RoomLayoutSpot): string {
  return spot.spotName;
}

export function normalizeSpot(raw: SpotIdentityWire): NormalizedSpot {
  const apiSpotId =
    ('spot_id' in raw ? idAsString(raw.spot_id) : null) ??
    ('id' in raw ? idAsString(raw.id) : null);
  const mapSpotName = 'spotName' in raw ? nonEmpty(raw.spotName) : null;
  const apiSpotName = 'spot_name' in raw ? nonEmpty(raw.spot_name) : null;
  const displayLabel = apiSpotName ?? mapSpotName;
  const gridKey = apiSpotId ?? mapSpotName ?? apiSpotName;
  return { apiSpotId, displayLabel, gridKey };
}

/** Same string as `reservedSpotKeys` and map selection (see {@link NormalizedSpot.gridKey}). */
export function getCanonicalSpotName(normalized: NormalizedSpot): string | null {
  return normalized.gridKey;
}

export function spotMatchesKey(spot: RoomLayoutSpot, key: string | null | undefined): boolean {
  const trimmed = key?.trim() ?? '';
  if (trimmed === '') return false;
  return spot.spotName === trimmed || spot.spot_name === trimmed;
}

export function resolveSpotKeyInLayout(
  spots: RoomLayoutSpot[],
  key: string | null | undefined
): string | null {
  const trimmed = key?.trim() ?? '';
  if (trimmed === '') return null;
  const match = spots.find((spot) => spotMatchesKey(spot, trimmed));
  return match?.spotName ?? null;
}
